/**
 * SoundManager - Audio System for T-Stacker
 * Manages Web Audio Context, ZzFX integration, and sound settings
 */

import * as SoundEffects from './SoundEffects';
import { ZZFX } from 'zzfx';
import uwuSampleUrl from '../assets/audio/uwu.mp3';

interface PitchableAudio extends HTMLAudioElement {
    mozPreservesPitch?: boolean;
    webkitPreservesPitch?: boolean;
}

export class SoundManager {
    private static instance: SoundManager;
    private masterVolume: number = 0.5;
    private sfxVolume: number = 0.5;
    private enabled: boolean = true;
    private warningCooldown: number = 0;
    private uwuCooldown: number = 0;
    private uwuFinaleCooldown: number = 0;
    private uwuSampleTemplate: HTMLAudioElement | null = null;

    private constructor() {}

    public static getInstance(): SoundManager {
        if (!SoundManager.instance) {
            SoundManager.instance = new SoundManager();
        }
        return SoundManager.instance;
    }

    public init(): void {
        // ZzFX handles audio initialization internally on first play
        // This is a no-op but kept for API compatibility
        if (typeof Audio !== 'undefined' && this.uwuSampleTemplate === null) {
            const audio = new Audio(uwuSampleUrl);
            audio.preload = 'auto';
            this.uwuSampleTemplate = audio;
            try {
                audio.load();
            } catch {
                // Ignore preload failures; we'll retry on first playback.
            }
        }
    }

    public resume(): void {
        // Resume ZzFX AudioContext if it exists and is suspended
        if (ZZFX.audioContext && ZZFX.audioContext.state === 'suspended') {
            ZZFX.audioContext.resume();
        }
    }

    public setMasterVolume(volume: number): void {
        this.masterVolume = Math.max(0, Math.min(1, volume));
    }

    public setSfxVolume(volume: number): void {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
    }

    public toggleSound(): boolean {
        this.enabled = !this.enabled;
        return this.enabled;
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    private play(config: any): void {
        if (!this.enabled || this.sfxVolume === 0) return;

        this.resume();

        // Apply volume settings
        const adjustedConfig = {
            ...config,
            volume: config.volume * this.sfxVolume * this.masterVolume
        };

        SoundEffects.playZzFX(adjustedConfig);
    }

    // ==================== UI SOUNDS ====================

    public playHover(): void {
        this.play(SoundEffects.uiSounds.hover);
    }

    public playClick(): void {
        this.play(SoundEffects.uiSounds.click);
    }

    public playBack(): void {
        this.play(SoundEffects.uiSounds.back);
    }

    public playMenuOpen(): void {
        this.play(SoundEffects.uiSounds.menuOpen);
    }

    // ==================== GAME SOUNDS ====================

    public playGrab(): void {
        this.play(SoundEffects.gameSounds.grab);
    }

    public playRelease(): void {
        this.play(SoundEffects.gameSounds.release);
    }

    public playImpact(): void {
        this.play(SoundEffects.gameSounds.impact);
    }

    public playThrow(): void {
        this.play(SoundEffects.gameSounds.throw);
    }

    // ==================== SCORING SOUNDS ====================

    public playTJunction(): void {
        this.play(SoundEffects.scoringSounds.tJunction);
    }

    public playUwu(): void {
        const now = Date.now();
        if (now - this.uwuCooldown < 220) return;
        this.uwuCooldown = now;

        this.playUwuSample({
            volume: 0.95,
            playbackRate: 1.04,
            randomRateJitter: 0.06,
        });
    }

    public playUwuFinale(): void {
        const now = Date.now();
        if (now - this.uwuFinaleCooldown < 1500) return;
        this.uwuFinaleCooldown = now;

        this.playUwuSample({
            volume: 1,
            playbackRate: 1.08,
            randomRateJitter: 0.03,
        });

        window.setTimeout(() => {
            this.playUwuSample({
                volume: 0.85,
                playbackRate: 1.28,
                randomRateJitter: 0.02,
            });
        }, 110);

        window.setTimeout(() => {
            this.playUwuSample({
                volume: 0.65,
                playbackRate: 0.92,
                randomRateJitter: 0.02,
            });
        }, 170);
    }

    public playScoreUp(): void {
        this.play(SoundEffects.scoringSounds.scoreUp);
    }

    // ==================== GAME STATE SOUNDS ====================

    public playGameStart(): void {
        this.play(SoundEffects.gameStateSounds.gameStart);
    }

    public playSettling(): void {
        this.play(SoundEffects.gameStateSounds.settling);
    }

    public playGameOver(): void {
        this.play(SoundEffects.gameStateSounds.gameOver);
    }

    // ==================== TIMER SOUNDS ====================

    public playWarning(): void {
        // Play warning every second, but with cooldown to prevent spam
        const now = Date.now();
        if (now - this.warningCooldown < 500) return;

        this.warningCooldown = now;
        this.play(SoundEffects.timerSounds.warning);
    }

    private playUwuSample(config: {
        volume: number;
        playbackRate: number;
        randomRateJitter: number;
    }): void {
        if (!this.enabled || this.sfxVolume === 0) return;
        if (typeof Audio === 'undefined') return;

        this.resume();

        const template = this.getUwuSampleTemplate();
        if (!template) return;

        const audio = template.cloneNode(true) as PitchableAudio;
        audio.currentTime = 0;
        audio.volume = Math.max(0, Math.min(1, config.volume * this.sfxVolume * this.masterVolume));

        const jitter = (Math.random() * 2 - 1) * config.randomRateJitter;
        audio.playbackRate = Math.max(0.5, Math.min(2, config.playbackRate + jitter));
        audio.preservesPitch = false;
        audio.mozPreservesPitch = false;
        audio.webkitPreservesPitch = false;

        void audio.play().catch(() => {
            // Ignore autoplay or decode errors; game remains playable.
        });
    }

    private getUwuSampleTemplate(): HTMLAudioElement | null {
        if (this.uwuSampleTemplate !== null) {
            return this.uwuSampleTemplate;
        }

        try {
            const audio = new Audio(uwuSampleUrl);
            audio.preload = 'auto';
            this.uwuSampleTemplate = audio;
            return audio;
        } catch {
            return null;
        }
    }
}

export const soundManager = SoundManager.getInstance();
